/**
 * Authentication Token Migration Utility
 * 
 * This utility handles the migration of authentication tokens from old storage keys
 * to new storage keys to ensure backward compatibility when the storage keys change.
 */

// Current storage keys
const CURRENT_TOKEN_KEY = 'token';
const CURRENT_REFRESH_TOKEN_KEY = 'refreshToken';
const CURRENT_USER_KEY = 'user';

// Legacy storage keys
const LEGACY_TOKEN_KEY = 'auth_token';
const LEGACY_REFRESH_TOKEN_KEY = 'refresh_token';
const LEGACY_USER_KEY = 'user'; // This one didn't change

// Migration flag to prevent multiple migrations
const MIGRATION_COMPLETE_KEY = 'auth_migration_complete';

/**
 * Check if migration has already been completed
 */
const isMigrationComplete = (): boolean => {
  return localStorage.getItem(MIGRATION_COMPLETE_KEY) === 'true';
};

/**
 * Mark migration as complete
 */
const markMigrationComplete = (): void => {
  localStorage.setItem(MIGRATION_COMPLETE_KEY, 'true');
};

/**
 * Migrate tokens from legacy storage keys to current keys
 * This function is safe to call multiple times
 */
export const migrateAuthTokens = (): void => {
  // Skip if migration already completed
  if (isMigrationComplete()) {
    return;
  }

  try {
    
    let migrationPerformed = false;

    // Check if we have any legacy tokens
    const legacyTokens = {
      localStorage: {
        token: localStorage.getItem(LEGACY_TOKEN_KEY),
        refreshToken: localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY),
        user: localStorage.getItem(LEGACY_USER_KEY)
      },
      sessionStorage: {
        token: sessionStorage.getItem(LEGACY_TOKEN_KEY),
        refreshToken: sessionStorage.getItem(LEGACY_REFRESH_TOKEN_KEY),
        user: sessionStorage.getItem(LEGACY_USER_KEY)
      }
    };

    // Check if we already have new tokens (to avoid overwriting)
    const currentTokens = {
      localStorage: {
        token: localStorage.getItem(CURRENT_TOKEN_KEY),
        refreshToken: localStorage.getItem(CURRENT_REFRESH_TOKEN_KEY),
        user: localStorage.getItem(CURRENT_USER_KEY)
      },
      sessionStorage: {
        token: sessionStorage.getItem(CURRENT_TOKEN_KEY),
        refreshToken: sessionStorage.getItem(CURRENT_REFRESH_TOKEN_KEY),
        user: sessionStorage.getItem(CURRENT_USER_KEY)
      }
    };

    // Migrate localStorage tokens
    if (legacyTokens.localStorage.token && !currentTokens.localStorage.token) {
      localStorage.setItem(CURRENT_TOKEN_KEY, legacyTokens.localStorage.token);
      localStorage.removeItem(LEGACY_TOKEN_KEY);
      migrationPerformed = true;
    }

    if (legacyTokens.localStorage.refreshToken && !currentTokens.localStorage.refreshToken) {
      localStorage.setItem(CURRENT_REFRESH_TOKEN_KEY, legacyTokens.localStorage.refreshToken);
      localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
      migrationPerformed = true;
    }

    // Migrate sessionStorage tokens
    if (legacyTokens.sessionStorage.token && !currentTokens.sessionStorage.token) {
      sessionStorage.setItem(CURRENT_TOKEN_KEY, legacyTokens.sessionStorage.token);
      sessionStorage.removeItem(LEGACY_TOKEN_KEY);
      migrationPerformed = true;
    }

    if (legacyTokens.sessionStorage.refreshToken && !currentTokens.sessionStorage.refreshToken) {
      sessionStorage.setItem(CURRENT_REFRESH_TOKEN_KEY, legacyTokens.sessionStorage.refreshToken);
      sessionStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
      migrationPerformed = true;
    }

    // Clean up any remaining legacy keys
    const remainingLegacyKeys = [
      LEGACY_TOKEN_KEY,
      LEGACY_REFRESH_TOKEN_KEY
    ];

    remainingLegacyKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
      }
      if (sessionStorage.getItem(key)) {
        sessionStorage.removeItem(key);
      }
    });

    // Mark migration as complete
    markMigrationComplete();

    if (migrationPerformed) {
      
      // Dispatch a custom event to notify components that migration is complete
      const migrationEvent = new CustomEvent('authMigrationComplete', {
        detail: { migrationPerformed }
      });
      window.dispatchEvent(migrationEvent);
    } else {
    }

  } catch (error) {
    console.error('❌ Failed to migrate authentication tokens:', error);
  }
};

/**
 * Force a re-migration by clearing the migration flag
 * This is useful for development/testing purposes
 */
export const resetMigration = (): void => {
  localStorage.removeItem(MIGRATION_COMPLETE_KEY);
};

/**
 * Get migration status information
 */
export const getMigrationStatus = () => {
  const hasLegacyTokens = !!(
    localStorage.getItem(LEGACY_TOKEN_KEY) ||
    localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY) ||
    sessionStorage.getItem(LEGACY_TOKEN_KEY) ||
    sessionStorage.getItem(LEGACY_REFRESH_TOKEN_KEY)
  );

  const hasCurrentTokens = !!(
    localStorage.getItem(CURRENT_TOKEN_KEY) ||
    localStorage.getItem(CURRENT_REFRESH_TOKEN_KEY) ||
    sessionStorage.getItem(CURRENT_TOKEN_KEY) ||
    sessionStorage.getItem(CURRENT_REFRESH_TOKEN_KEY)
  );

  return {
    migrationComplete: isMigrationComplete(),
    hasLegacyTokens,
    hasCurrentTokens,
    needsMigration: hasLegacyTokens && !hasCurrentTokens
  };
};

// Auto-run migration when this module is imported
migrateAuthTokens();
